import { useEffect, useMemo, useRef } from "react";
import {
  AlertTriangle,
  Bell,
  CheckCheck,
  Inbox,
  RefreshCw,
  X,
} from "lucide-react";
import { Skeleton } from "@mbbe/ui";
import {
  NotificationItem,
  getNotificationLifecycle,
  isActionRequiredNotification,
} from "./NotificationItem";
import { PushPermissionPrompt } from "./PushPermissionPrompt";
import type {
  NotificationFilter,
  useCustomerNotifications,
} from "../hooks/useCustomerNotifications";
import { useLanguage } from "../../contexts/LanguageContext";

type CustomerNotificationsState = ReturnType<typeof useCustomerNotifications>;

interface NotificationPanelProps {
  open: boolean;
  bellRef: React.RefObject<HTMLButtonElement | null>;
  filter: NotificationFilter;
  onFilterChange: (filter: NotificationFilter) => void;
  notifications: CustomerNotificationsState;
  onClose: () => void;
  resolveIssueHref?: (issueId: string, linkPath?: string | null) => string;
}

const FILTERS: { key: NotificationFilter; labelKey: string }[] = [
  { key: "all", labelKey: "destek_filter_all" },
  { key: "unread", labelKey: "destek_filter_unread" },
  { key: "action_required", labelKey: "destek_filter_action" },
];

const LIFECYCLE_GROUPS = [
  {
    key: "action-required",
    labelKey: "destek_group_action",
    accent: "bg-yellow-300",
  },
  { key: "team-reply", labelKey: "destek_group_reply", accent: "bg-brand-300" },
  {
    key: "completed",
    labelKey: "destek_group_completed",
    accent: "bg-emerald-300",
  },
  {
    key: "rejected-error",
    labelKey: "destek_group_rejected",
    accent: "bg-red-300",
  },
] as const;

const EMPTY_COPY = {
  tr: {
    all: {
      title: "Henüz bildirim yok.",
      detail:
        "Proje, görev, ödeme, rapor ve destek güncellemeleri burada görünür.",
    },
    unread: {
      title: "Okunmamış bildirim yok.",
      detail:
        "Yeni bir proje veya iletişim güncellemesi geldiğinde bu görünüm yenilenir.",
    },
    action_required: {
      title: "Aksiyon bekleyen kayıt yok.",
      detail:
        "Yanıtınız veya işleminiz gereken kayıtlar bu görünümde ayrı tutulur.",
    },
  },
  en: {
    all: {
      title: "No notifications yet.",
      detail:
        "Project, task, payment, report, and support updates will appear here.",
    },
    unread: {
      title: "No unread notifications.",
      detail:
        "This view updates when a new project or communication update arrives.",
    },
    action_required: {
      title: "No action-required requests.",
      detail:
        "Items that require your response or action are kept separate in this view.",
    },
  },
};

function NotificationSkeleton() {
  const { t } = useLanguage();
  return (
    <div className="space-y-2" aria-label={t("destek_loading_notifications")}>
      {Array.from({ length: 4 }).map((_, index) => (
        <div
          key={index}
          className="rounded-lg border border-white/5 bg-slate-950/35 p-3 pl-4"
        >
          <div className="flex gap-3">
            <Skeleton variant="rectangular" className="h-8 w-8 shrink-0" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-3 w-7/12" />
              <Skeleton className="h-2.5 w-10/12" />
              <Skeleton className="h-2.5 w-5/12" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyNotifications({ filter }: { filter: NotificationFilter }) {
  const { language } = useLanguage();
  const copy = EMPTY_COPY[language][filter];

  return (
    <div className="rounded-lg border border-dashed border-white/10 bg-slate-950/35 px-5 py-8 text-center">
      <Inbox size={24} className="mx-auto text-slate-500" />
      <div className="mt-3 text-sm font-semibold text-slate-200">
        {copy.title}
      </div>
      <p className="mx-auto mt-1 max-w-[18rem] text-xs leading-relaxed text-slate-500">
        {copy.detail}
      </p>
    </div>
  );
}

export function NotificationPanel({
  open,
  bellRef,
  filter,
  onFilterChange,
  notifications,
  onClose,
  resolveIssueHref,
}: NotificationPanelProps) {
  const { t } = useLanguage();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { markAllSeen } = notifications;

  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        bellRef.current?.focus();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Pointer event cast to MouseEvent/TouchEvent dynamically
    function onPointerDown(event: MouseEvent | TouchEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (
        panelRef.current?.contains(target) ||
        bellRef.current?.contains(target)
      )
        return;
      onClose();
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [bellRef, onClose, open]);

  useEffect(() => {
    if (!open) return;
    void markAllSeen();
  }, [markAllSeen, open]);

  const hasUnread = notifications.unreadCount > 0;
  const { filteredItems, groupedItems } = useMemo(() => {
    const nextFilteredItems = notifications.items.filter((notification) => {
      if (filter === "unread") return !notification.readAt;
      if (filter === "action_required")
        return isActionRequiredNotification(notification);
      return true;
    });

    return {
      filteredItems: nextFilteredItems,
      groupedItems: LIFECYCLE_GROUPS.map((group) => ({
        ...group,
        items: nextFilteredItems.filter(
          (notification) =>
            getNotificationLifecycle(notification) === group.key,
        ),
      })).filter((group) => group.items.length > 0),
    };
  }, [filter, notifications.items]);

  const getFilterCount = (filterOption: NotificationFilter) => {
    if (filterOption === "unread") return notifications.unreadCount;
    if (filterOption === "all" && filter === "all")
      return notifications.total || notifications.items.length;
    return null;
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-sm sm:hidden"
        aria-hidden="true"
      />
      <section
        ref={panelRef}
        data-toast-bottom-surface=""
        role="dialog"
        aria-modal="true"
        aria-labelledby="destek-notifications-title"
        className="fixed inset-x-0 bottom-0 z-50 max-h-[86vh] rounded-t-xl border border-white/10 bg-dark-900/95 shadow-2xl shadow-black/40 backdrop-blur-xl sm:absolute sm:inset-auto sm:right-0 sm:top-[calc(100%+0.75rem)] sm:w-[27rem] sm:max-w-[calc(100vw-2rem)] sm:rounded-lg"
      >
        <div className="border-b border-white/5 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2
                id="destek-notifications-title"
                className="flex items-center gap-2 text-sm font-semibold text-white"
              >
                <Bell size={16} className="text-brand-200" />
                {t("destek_notifications")}
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                {hasUnread
                  ? `${notifications.unreadCount} ${t("destek_unread_count")}`
                  : t("destek_no_unread")}
              </p>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={() => {
                onClose();
                bellRef.current?.focus();
              }}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
              aria-label={t("destek_close_panel")}
              title={t("destek_help_close_aria")}
            >
              <X size={16} />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => notifications.refetch()}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-white/10 px-3 text-xs font-medium text-slate-300 transition-colors hover:border-brand-400/40 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
            >
              <RefreshCw size={13} />
              {t("destek_refresh")}
            </button>
            <button
              type="button"
              onClick={() => notifications.markAllRead()}
              disabled={!hasUnread || notifications.isMarkingAllRead}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-white/10 px-3 text-xs font-medium text-slate-300 transition-colors hover:border-brand-400/40 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-45"
            >
              <CheckCheck size={13} />
              {t("destek_mark_all_read")}
            </button>
          </div>
          <div
            className="mt-3 grid grid-cols-3 gap-1 rounded-lg border border-white/10 bg-slate-950/55 p-1"
            role="tablist"
            aria-label={t("destek_filter_aria")}
          >
            {FILTERS.map((filterOption) => {
              const selected = filter === filterOption.key;
              return (
                <button
                  key={filterOption.key}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => onFilterChange(filterOption.key)}
                  className={`min-h-11 rounded-md px-2 text-[11px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 ${
                    selected
                      ? "bg-white/10 text-white shadow-sm shadow-black/20"
                      : "text-slate-500 hover:bg-white/[0.04] hover:text-slate-200"
                  }`}
                >
                  <span className="block truncate">
                    {t(filterOption.labelKey)}
                  </span>
                  {getFilterCount(filterOption.key) !== null && (
                    <span className="mt-0.5 block text-[10px] font-medium tabular-nums opacity-70">
                      {getFilterCount(filterOption.key)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="max-h-[calc(86vh-11.5rem)] overflow-y-auto p-3 sm:max-h-[32rem]">
          <div className="mb-3">
            <PushPermissionPrompt
              permission={notifications.pushPermission}
              isRequesting={notifications.isRequestingPushPermission}
              error={notifications.pushPermissionError}
              onRequestPermission={() => notifications.requestPushPermission()}
            />
          </div>

          {notifications.isLoading ? (
            <NotificationSkeleton />
          ) : notifications.isError ? (
            <div className="rounded-lg border border-red-400/25 bg-red-500/10 p-4 text-sm text-red-100">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">
                    {t("destek_notifications_load_error")}
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-red-100/75">
                    {t("destek_notifications_error_detail")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => notifications.refetch()}
                className="mt-3 inline-flex min-h-11 items-center gap-1.5 rounded-md border border-red-300/25 px-3 text-xs font-semibold text-red-50 transition-colors hover:bg-red-400/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300/50"
              >
                <RefreshCw size={13} />
                {t("destek_try_again")}
              </button>
            </div>
          ) : filteredItems.length === 0 ? (
            <EmptyNotifications filter={filter} />
          ) : (
            <div className="space-y-4">
              {groupedItems.map((group) => (
                <section key={group.key} aria-label={t(group.labelKey)}>
                  <div className="mb-2 flex items-center gap-2 px-1">
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${group.accent}`}
                      aria-hidden="true"
                    />
                    <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                      {t(group.labelKey)}
                    </h3>
                    <span className="ml-auto text-[11px] tabular-nums text-slate-600">
                      {group.items.length}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {group.items.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkRead={notifications.markRead}
                        onOpenIssue={onClose}
                        resolveIssueHref={resolveIssueHref}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
